/**
 * CRE Score panel — visual RAG-coded scorecard rendered on the war-room
 * incident page. Reads from `incident.cre_score` (persisted with the
 * incident document). Pure presentational; never mutates the incident.
 */
import { Sparkles, ShieldCheck, Info, RotateCcw } from "lucide-react";

const RAG_STYLES = {
  green: {
    bar: "bg-emerald-500",
    ring: "stroke-emerald-500",
    pill: "bg-emerald-50 border-emerald-200 text-emerald-700",
    dot: "bg-emerald-500",
    softBg: "bg-emerald-50/60",
    label: "Strong",
  },
  amber: {
    bar: "bg-amber-500",
    ring: "stroke-amber-500",
    pill: "bg-amber-50 border-amber-200 text-amber-700",
    dot: "bg-amber-500",
    softBg: "bg-amber-50/60",
    label: "Moderate",
  },
  red: {
    bar: "bg-rose-500",
    ring: "stroke-rose-500",
    pill: "bg-rose-50 border-rose-200 text-rose-700",
    dot: "bg-rose-500",
    softBg: "bg-rose-50/60",
    label: "Weak",
  },
};

function ScoreRing({ score, rag, size = 168 }) {
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, (Number(score) || 0) / 10));
  const dash = c * pct;
  const styles = RAG_STYLES[rag] || RAG_STYLES.red;
  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      data-testid="cre-score-ring"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="white"
          stroke="#e2e8f0"
          strokeWidth={stroke}
        />
        {/* Progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          className={styles.ring}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          data-testid="cre-score-overall"
          className="font-heading font-black tracking-tight text-slate-900 text-4xl sm:text-[40px] leading-none"
        >
          {Number(score).toFixed(1)}
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-1">
          / 10
        </div>
      </div>
    </div>
  );
}

function ParameterCard({ component }) {
  const styles = RAG_STYLES[component.rag] || RAG_STYLES.red;
  // Score bar width: 0-10 -> 0-100%
  const widthPct = `${Math.max(0, Math.min(100, (component.score / 10) * 100))}%`;
  return (
    <div
      data-testid={`cre-score-param-${component.key}`}
      className="rounded-xl border border-slate-200 bg-white p-3.5 sm:p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] sm:text-sm font-semibold text-slate-900">
          {component.name}
        </div>
        <span
          data-testid={`cre-score-param-${component.key}-pill`}
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-semibold ${styles.pill}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
          {component.label}
        </span>
      </div>

      <div className="mt-2 flex items-baseline gap-1.5">
        <div
          data-testid={`cre-score-param-${component.key}-score`}
          className="font-heading font-bold text-slate-900 text-xl tracking-tight"
        >
          {Number(component.score).toFixed(1)}
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">
          / 10
        </div>
      </div>

      <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${styles.bar} rounded-full transition-[width] duration-500`}
          style={{ width: widthPct }}
          aria-hidden
        />
      </div>

      <p className="mt-2.5 text-[12px] text-slate-600 leading-relaxed">
        {component.explanation}
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="cre-score-empty"
      className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-slate-500" strokeWidth={1.75} />
        </div>
        <div>
          <div className="font-heading font-bold text-slate-900 text-base">
            CRE Score
          </div>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            CRE Score not available for this incident. Re-run analysis to
            generate score.
          </p>
          <p className="mt-1.5 text-[12px] text-slate-500 flex items-center gap-1.5">
            <RotateCcw className="h-3 w-3" strokeWidth={1.75} />
            Use the "Re-run analysis" button at the top of the page.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function CREScorePanel({ score }) {
  if (!score) {
    return <EmptyState />;
  }

  const styles = RAG_STYLES[score.overall_rag] || RAG_STYLES.red;

  return (
    <section data-testid="cre-score-panel" className="space-y-3 sm:space-y-4">
      {/* Main score card */}
      <div
        className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
        data-testid="cre-score-main-card"
      >
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-sky-500/10 via-cyan-500/10 to-violet-500/10 border border-sky-200 flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 text-sky-600" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-heading font-bold tracking-tight text-slate-900 text-sm sm:text-base">
              CRE Score
            </div>
            <p className="text-[11.5px] sm:text-[12px] text-slate-500">
              Incident reasoning quality, confidence, safety, and approval
              readiness
            </p>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 sm:py-6 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-5 md:gap-7 items-center">
          {/* Ring */}
          <div className="flex flex-col items-center md:items-start gap-2">
            <ScoreRing
              score={score.overall_score}
              rag={score.overall_rag}
              size={172}
            />
            <span
              data-testid="cre-score-band-pill"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] font-semibold ${styles.pill}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
              <span data-testid="cre-score-band-label">
                {score.overall_label}
              </span>
            </span>
          </div>

          {/* Score summary */}
          <div className="space-y-2.5 w-full">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
              Score summary
            </div>
            <SummaryRow
              tone="green"
              label="Main strength"
              value={score.main_strength}
              testid="cre-score-summary-strength"
            />
            <SummaryRow
              tone="amber"
              label="Main gap"
              value={score.main_gap}
              testid="cre-score-summary-gap"
            />
            <SummaryRow
              tone="sky"
              label="Suggested improvement"
              value={score.suggested_improvement}
              testid="cre-score-summary-improvement"
            />
          </div>
        </div>
      </div>

      {/* Legend */}
      <div
        data-testid="cre-score-legend"
        className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-slate-600"
      >
        <span className="inline-flex items-center gap-1.5">
          <Info className="h-3 w-3 text-slate-400" strokeWidth={2} />
          <span className="font-semibold text-slate-700">RAG legend</span>
        </span>
        <LegendDot color="green" text="Strong reasoning readiness" />
        <LegendDot color="amber" text="Useful but needs review" />
        <LegendDot color="red" text="Weak or incomplete reasoning" />
      </div>

      {/* 10 parameters grid */}
      <div
        data-testid="cre-score-params-grid"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3"
      >
        {(score.components || []).map((c) => (
          <ParameterCard key={c.key} component={c} />
        ))}
      </div>

      {/* Disclaimer */}
      <p
        data-testid="cre-score-disclaimer"
        className="text-[11.5px] sm:text-[12px] text-slate-500 leading-relaxed flex items-start gap-1.5 px-1"
      >
        <ShieldCheck
          className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5"
          strokeWidth={1.75}
        />
        <span>
          CRE Score indicates reasoning completeness and readiness based on
          available information. It does not guarantee root cause accuracy.
          Human validation is required.
        </span>
      </p>
    </section>
  );
}

function SummaryRow({ tone, label, value, testid }) {
  const toneStyles = {
    green: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
    amber: "border-amber-200 bg-amber-50/60 text-amber-700",
    sky: "border-sky-200 bg-sky-50/60 text-sky-700",
  };
  const dotStyles = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    sky: "bg-sky-500",
  };
  return (
    <div
      data-testid={testid}
      className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border ${toneStyles[tone] || toneStyles.sky}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full mt-2 shrink-0 ${dotStyles[tone] || dotStyles.sky}`} />
      <div className="min-w-0">
        <div className="text-[10.5px] font-mono uppercase tracking-wider opacity-80">
          {label}
        </div>
        <div className="mt-0.5 text-[13px] text-slate-800 leading-snug">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, text }) {
  const styles = RAG_STYLES[color] || RAG_STYLES.red;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
      <span>{text}</span>
    </span>
  );
}
